import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { FlagType, userType } from '../../../shared/utils/enums';
import { CookieService } from 'ngx-cookie-service';
import { EstablishmentService } from '../../../services/establishment.service';
import { FlagService } from '../../../shared/services/flag.service';

interface TwoFactorState {
  challengeId: string;
  maskedEmail: string;
  expiresAt: number;
}

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent implements OnInit, OnDestroy {
  form: FormGroup = this.fb.group({
    email: ['', Validators.required],
    password: ['', [Validators.required]],
  });
  codeForm: FormGroup = this.fb.group({
    code: ['', [Validators.required, Validators.pattern(/^\s*\d{6}\s*$/)]],
  });

  loading = false;
  user: any;

  /** Second step (agency accounts): the e-mailed one-time code. */
  twoFactor: TwoFactorState | null = null;
  resendIn = 0;
  codeError = '';
  private countdown: any;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private cookieService: CookieService,
    private flag: FlagService,
    private establishmentService: EstablishmentService
  ) {}

  ngOnInit() {}

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  get passwordFormField() {
    return this.form.get('password');
  }

  handleLogin() {
    if (this.form.invalid || this.loading) {
      return;
    }
    this.loading = true;

    this.authService.login(this.form.value).subscribe({
      next: (value) => {
        if (value?.two_factor_required) {
          this.startTwoFactor(value);
          this.loading = false;
          return;
        }
        this.completeLogin(value);
      },
      error: (e) => {
        const message = e?.error?.message;
        let msg = 'Les informations saisies sont incorrectes';
        if (message === 'User is disabled') {
          msg = "Votre compte n'est pas actif (invitation non acceptée ou compte désactivé). Contactez votre administrateur.";
        } else if (message === 'User account is locked') {
          msg = 'Trop de tentatives échouées : votre compte est temporairement verrouillé. Réessayez dans 10 minutes.';
        } else if (e?.status === 503 && message) {
          msg = message;
        }
        this.flag.displayFlag(msg, FlagType.error);
        this.loading = false;
      },
    });
  }

  // ─────────────────────────── 2FA ───────────────────────────

  private startTwoFactor(challenge: any) {
    this.twoFactor = {
      challengeId: challenge.challenge_id,
      maskedEmail: challenge.masked_email,
      expiresAt: Date.now() + (challenge.expires_in_seconds ?? 300) * 1000,
    };
    this.codeError = '';
    this.codeForm.reset();
    this.startCountdown(challenge.resend_cooldown_seconds ?? 60);
  }

  verifyCode() {
    if (!this.twoFactor || this.codeForm.invalid || this.loading) {
      return;
    }
    this.loading = true;
    this.codeError = '';
    const code = String(this.codeForm.value.code ?? '').replace(/\s/g, '');

    this.authService.verifyTwoFactor(this.twoFactor.challengeId, code).subscribe({
      next: (value) => {
        this.stopCountdown();
        this.completeLogin(value);
      },
      error: (e) => {
        this.loading = false;
        const body = e?.error ?? {};
        if (body.restart_login) {
          this.backToCredentials(body.message || 'Votre session de vérification a expiré. Veuillez vous reconnecter.');
          return;
        }
        if (body.code === 'INVALID_CODE') {
          const remaining = body.remaining_attempts;
          this.codeError = remaining != null
            ? `Code incorrect. ${remaining} tentative${remaining > 1 ? 's' : ''} restante${remaining > 1 ? 's' : ''}.`
            : 'Code incorrect.';
        } else {
          this.codeError = body.message || 'La vérification a échoué. Veuillez réessayer.';
        }
      },
    });
  }

  resendCode() {
    if (!this.twoFactor || this.resendIn > 0) {
      return;
    }
    this.authService.resendTwoFactor(this.twoFactor.challengeId).subscribe({
      next: (challenge) => {
        this.twoFactor = {
          challengeId: challenge.challenge_id,
          maskedEmail: challenge.masked_email,
          expiresAt: Date.now() + (challenge.expires_in_seconds ?? 300) * 1000,
        };
        this.codeError = '';
        this.flag.displayFlag('Un nouveau code vous a été envoyé.', FlagType.success);
        this.startCountdown(challenge.resend_cooldown_seconds ?? 60);
      },
      error: (e) => {
        const body = e?.error ?? {};
        if (body.retry_after_seconds) {
          this.startCountdown(body.retry_after_seconds);
          return;
        }
        if (body.restart_login) {
          this.backToCredentials(body.message || 'Veuillez vous reconnecter.');
          return;
        }
        this.codeError = body.message || "Le code n'a pas pu être renvoyé.";
      },
    });
  }

  backToCredentials(message?: string) {
    this.stopCountdown();
    this.twoFactor = null;
    this.codeError = '';
    this.codeForm.reset();
    this.form.get('password')?.reset();
    this.loading = false;
    if (message) {
      this.flag.displayFlag(message, FlagType.error);
    }
  }

  private startCountdown(seconds: number) {
    this.stopCountdown();
    this.resendIn = Math.max(0, Math.ceil(seconds));
    this.countdown = setInterval(() => {
      this.resendIn = Math.max(0, this.resendIn - 1);
      if (this.resendIn === 0) {
        this.stopCountdown();
      }
    }, 1000);
  }

  private stopCountdown() {
    if (this.countdown) {
      clearInterval(this.countdown);
      this.countdown = null;
    }
  }

  // ─────────────────────────── session ───────────────────────────

  private completeLogin(value: any) {
    // Only AGENCY and AGENCY_USER accounts may sign in on the agency app.
    const kind = value?.user?.kind;
    if (kind !== userType.agence && kind !== userType.agence_user) {
      this.flag.displayFlag(
        "Ce compte n'est pas autorisé à se connecter sur cet espace.",
        FlagType.error
      );
      this.backToCredentials();
      return;
    }
    this.user = value.user;
    localStorage.setItem('user', JSON.stringify(this.user));
    this.cookieService.set('token', value.token.token);
    localStorage.setItem('token', value.token.token);
    localStorage.setItem('token_expiry', String(value.token.expiry));
    // Agency screens use this as the AGENCY id (missions, candidatures...). A collaborator acts
    // on behalf of its agency, so it must be agency_id, not the personal user id.
    const agencyId = Number(this.user.agency_id) > 0 ? Number(this.user.agency_id) : this.user.id;
    localStorage.setItem('id', String(agencyId));
    this.authService.setUser(this.user);

    this.establishmentService.getAgencyById(agencyId).subscribe({
      next: (res: any) => {
        localStorage.setItem('agency', JSON.stringify({
          id: res?.id ?? agencyId,
          name: res?.name,
          address: res?.address,
          email: res?.email,
          phone: res?.phone,
          siret: res?.siret,
        }));
        this.loading = false;
        this.router.navigateByUrl('/agency/dash');
      },
      error: () => {
        // Agency details are only used for display: never block a valid session on them.
        localStorage.setItem('agency', JSON.stringify({ id: agencyId }));
        this.loading = false;
        this.router.navigateByUrl('/agency/dash');
      },
    });
  }
}
